// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll, { FancyScrollRefProps } from "@conklin/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@conklin/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@conklin/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@conklin/App/Components/Notify/Notify";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import {
    canCheckoutWithCart,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
} from "@conklin/App/Store/Selectors/CartSelectors";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import { parseBoolean } from "@conklin/XDL/Utilities";
import { mergeWithCss } from "@conklin/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canSubmitRequisition,
    getCurrentCartState,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    isPopupShown?: boolean;
    isAddToCartMessage?: boolean;
    filterLines?: string[];
    qtyAdjusted?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const { isUpdatingCart } = state.context;
    // XNG-Changes
    const isAuthenticated = xIsAuthenticated(state);
    return {
        cart,
        isCartLoading: cartState.isLoading,
        isCartCheckoutDisabled:
            cartState.isLoading ||
            !cart ||
            // XNG-Changes
            isCartCheckoutDisabled(cart, isAuthenticated) ||
            isUpdatingCart ||
            parseBoolean(cart?.properties.isInternationalOrder),
        canSubmitRequisition: canSubmitRequisition(cart),
        // XNG-Changes
        canCheckoutWithCart: canCheckoutWithCart(cart, isAuthenticated),
        isPunchOutOrder: isPunchOutOrder(cart),
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        // XNG-Changes
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart, isAuthenticated),
        // XNG-Changes
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart, isAuthenticated),
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
        isAuthenticated,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        rememberMe: getSession(state).rememberMe,
        onePageCheckout: getSettingsCollection(state).cartSettings.onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
    signInAsGuest,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    wrapper?: InjectableCss;
    centerWrapper?: InjectableCss;
    notifyWrapper?: InjectableCss;
    noCartLinesNotify?: TypographyProps;
    totalWrapper?: InjectableCss;
    title?: TypographyProps;
    totalItems?: TypographyProps;
    cartLinesContainer?: InjectableCss;
    cartLinesWrapper?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    bottomWrapper?: InjectableCss;
    bottomSectionWrapper?: InjectableCss;
    itemsCount?: InjectableCss;
    totalItem?: InjectableCss;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    checkoutButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    addToCartMessage?: TypographyProps;
    totalCountValue?: TypographyProps;
}

export const miniCartStyles: MiniCartStyles = {};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    isCartLoading,
    cartPageUrl,
    requisitionConfirmationPageUrl,
    isPunchOutOrder,
    isCartCheckoutDisabled,
    totalCountDisplay,
    orderSubTotalDisplay,
    removeCartLine,
    updateCartLine,
    submitRequisition,
    preloadCheckoutShippingData,
    canSubmitRequisition,
    doPunchOutCheckout,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isUpdatingCartLine,
    isRemovingCartLine,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    isPopupShown,
    canCheckoutWithCart,
    isAuthenticated,
    rememberMe,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInPageUrl,
    signInAsGuest,
    filterLines = [],
    qtyAdjusted,
    isAddToCartMessage = false,
    history,
}) => {
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (!cart) {
            return;
        }
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        // if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
        //     setQuoteRequiredModalIsOpen(true);
        //     return;
        // }

        setQuoteRequiredModalIsOpen(false);
        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;

        if (onePageCheckout && onePageCheckoutPageUrl && signInPageUrl) {
            const onePageCheckoutUrl = isAwaitingApproval
                ? `${onePageCheckoutPageUrl}?cartId=${cartId}`
                : onePageCheckoutPageUrl;
            if (!isAuthenticated && !rememberMe) {
                history.push(`${signInPageUrl}?returnUrl=${onePageCheckoutUrl}`);
            } else {
                history.push(onePageCheckoutUrl);
            }
            return;
        }

        setIsCheckoutLoading(true);

        if (canBypassCheckoutAddress) {
            setIsCheckoutLoading(false);

            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: cart?.isAwaitingApproval ? cart.id : undefined,
            onSuccess: () => {
                setIsCheckoutLoading(false);
                history.push(
                    cart?.isAwaitingApproval
                        ? `${checkoutShippingPageUrl}?cartId=${cart.id}`
                        : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: any) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        history.push(cartPageUrl);
    };

    if (!cart && isCartLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!cart) {
        return null;
    }

    const cartLines = cart.cartLines ?? [];

    if (!cartLines?.length) {
        return (
            <StyledWrapper {...mergeWithCss(styles.wrapper, styles.notifyWrapper)}>
                <Notify {...styles.noCartLinesNotify} type="info" showClose={false}>
                    {siteMessage("Custom_Cart_Empty_Message")}
                </Notify>
            </StyledWrapper>
        );
    }

    return (
        <>
            <LoadingOverlay
                {...styles.loadingOverlay}
                loading={(isCartLoading || isUpdatingCartLine || isRemovingCartLine) && !isAddToCartMessage}
            >
                <StyledWrapper {...styles.cartLinesContainer}>
                    <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef} shadowHeight="45px">
                        <StyledWrapper
                            {...styles.cartLinesWrapper}
                            ref={parentBoxRef}
                            data-test-selector="cartLines-container"
                        >
                            {isAddToCartMessage && (
                                <Typography {...styles.addToCartMessage}>
                                    {qtyAdjusted
                                        ? siteMessage("Cart_QuantityAdjusted")
                                        : siteMessage("Cart_ProductAddedToCart")}
                                </Typography>
                            )}
                            {!isAddToCartMessage && parseBoolean(cart.properties.hasUnEligibleQtyProduct) && (
                                <Notify type="warning" showClose={false} fullWidth={true}>
                                    {siteMessage("Custom_Cart_HasUnEligibleQtyProduct")}
                                </Notify>
                            )}
                            {createWidgetElement("Common/CartApproverMessage", {
                                fields: {},
                            })}
                            {cartLines &&
                                cartLines
                                    ?.filter(c => {
                                        if (!filterLines.length) {
                                            return true;
                                        }
                                        return filterLines.includes(c.productId || "");
                                    })
                                    .map(cartLine => {
                                        const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                                        return (
                                            <MiniCartLineCard
                                                key={`miniCartLine_${cartLine.id}`}
                                                cartLine={cartLine}
                                                cart={cart}
                                                qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                                removeCartLineClickHandler={removeCartLineClickHandler}
                                                errorMessages={errorMessages}
                                                showRemove={!isAddToCartMessage}
                                                qtyDisabled={isAddToCartMessage}
                                            />
                                        );
                                    })}
                        </StyledWrapper>
                    </FancyScroll>
                </StyledWrapper>
                <StyledWrapper {...styles.bottomWrapper}>
                    <StyledWrapper {...styles.bottomSectionWrapper}>
                        {!isAddToCartMessage && (
                            <StyledWrapper {...styles.itemsCount}>
                                <Typography {...styles.totalLabel}>{`${translate("Items")}:`}</Typography>
                                <Typography {...styles.totalCountValue}>{totalCountDisplay}</Typography>
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.totalItem}>
                            <Typography {...styles.totalLabel}>{`${translate("Subtotal")}:`}</Typography>
                            <Typography {...styles.totalValue}>{orderSubTotalDisplay}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.bottomSectionWrapper}>
                        <Button {...styles.viewCartButton} onClick={handleViewCarClick}>
                            {translate("View Cart")}
                        </Button>
                        {canCheckoutWithCart && (
                            <>
                                {!canSubmitRequisition ? (
                                    <Button
                                        {...styles.checkoutButton}
                                        onClick={checkoutHandler}
                                        disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
                                    >
                                        {cart?.requiresApproval
                                            ? translate("Checkout for Approval")
                                            : isPunchOutOrder
                                              ? translate("Return to Procurement System")
                                              : translate("Continue to Checkout")}
                                    </Button>
                                ) : (
                                    <Button
                                        {...styles.checkoutButton}
                                        onClick={submitRequisitionClickHandler}
                                        data-test-selector="cartTotal_SubmitRequisition"
                                        disabled={parseBoolean(cart.properties.erpSystemIsDown)}
                                    >
                                        {translate("Submit Requisition")}
                                    </Button>
                                )}
                            </>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </LoadingOverlay>
            <TwoButtonModal
                headlineText={siteMessage("Custom_RequiredQuote_Modal_Title")}
                modalIsOpen={quoteRequiredModalIsOpen}
                messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={() => setQuoteRequiredModalIsOpen(false)}
                onSubmit={checkoutHandler}
                submitTestSelector="QuoteInCartMessageSubmit"
                dataTestSelector="twoButtonModal_RequiresQuote"
            ></TwoButtonModal>
        </>
    );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
